import { usePlayer } from '../../../context/PlayerContext'
import { Button, IconButton } from '../../common'
import { cn } from '../../../utils'
import styles from './QueuePanel.module.css'

function formatDuration(seconds: number): string {
  if (!seconds) return '--:--'
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

export function QueuePanel() {
  const {
    queue,
    currentIndex,
    isQueueOpen,
    closeQueue,
    clearQueue,
    removeFromQueue,
    playFromIndex,
    isPlaying,
    currentTrack,
  } = usePlayer()

  // Calculate total queue duration
  const totalDuration = queue.reduce((sum, track) => sum + (track.duration || 0), 0)
  const remainingDuration = queue
    .slice(currentIndex + 1)
    .reduce((sum, track) => sum + (track.duration || 0), 0)

  return (
    <>
      {/* Backdrop */}
      <div
        className={cn(styles.backdrop, isQueueOpen && styles.visible)}
        onClick={closeQueue}
      />

      {/* Panel */}
      <div className={cn(styles.panel, isQueueOpen && styles.open)}>
        <div className={styles.header}>
          <div className={styles.headerTitle}>
            <h2>
              <i className="bi bi-music-note-list"></i>
              Queue
            </h2>
            <span className={styles.queueInfo}>
              {queue.length} tracks · {formatDuration(totalDuration)}
            </span>
          </div>
          <IconButton variant="ghost" icon="bi bi-x-lg" aria-label="Close queue" onClick={closeQueue} />
        </div>

        {/* Now Playing Section */}
        {currentTrack && (
          <div className={styles.nowPlaying}>
            <div className={styles.sectionLabel}>Now Playing</div>
            <div className={cn(styles.track, styles.current)}>
              <div className={styles.trackNumber}>
                <i className={`bi ${isPlaying ? 'bi-soundwave' : 'bi-pause-fill'}`}></i>
              </div>
              {currentTrack.thumbnail ? (
                <img src={currentTrack.thumbnail} alt="" className={styles.trackThumb} />
              ) : (
                <div className={styles.noThumb}>
                  <i className="bi bi-music-note"></i>
                </div>
              )}
              <div className={styles.trackInfo}>
                <div className={styles.trackTitle}>{currentTrack.title}</div>
                <div className={styles.trackMeta}>{currentTrack.artist}</div>
              </div>
              <span className={styles.trackDuration}>
                {formatDuration(currentTrack.duration)}
              </span>
            </div>
          </div>
        )}

        {/* Up Next Section */}
        <div className={styles.body}>
          <div className={styles.sectionHeader}>
            <span className={styles.sectionLabel}>
              Up Next ({queue.length - currentIndex - 1} tracks · {formatDuration(remainingDuration)})
            </span>
            {queue.length > 1 && (
              <Button variant="ghost" size="sm" onClick={clearQueue}>Clear All</Button>
            )}
          </div>

          {queue.length === 0 ? (
            <div className={styles.empty}>
              <i className="bi bi-music-note-list"></i>
              <p>Your queue is empty</p>
              <p className={styles.emptyHint}>
                Add releases from the Releases page to start listening
              </p>
            </div>
          ) : (
            <div className={styles.tracks}>
              {queue.slice(currentIndex + 1).map((track, idx) => {
                const actualIndex = currentIndex + 1 + idx
                return (
                  <div
                    key={track.id}
                    className={styles.track}
                    onClick={() => playFromIndex(actualIndex)}
                  >
                    <div className={styles.trackNumber}>{actualIndex + 1}</div>
                    {track.thumbnail ? (
                      <img src={track.thumbnail} alt="" className={styles.trackThumb} />
                    ) : (
                      <div className={styles.noThumb}>
                        <i className="bi bi-music-note"></i>
                      </div>
                    )}
                    <div className={styles.trackInfo}>
                      <div className={styles.trackTitle}>{track.title}</div>
                      <div className={styles.trackMeta}>
                        {track.artist} · {track.releaseTitle}
                      </div>
                    </div>
                    <span className={styles.trackDuration}>
                      {formatDuration(track.duration)}
                    </span>
                    <IconButton
                      variant="ghost"
                      size="sm"
                      icon="bi bi-x"
                      aria-label="Remove from queue"
                      title="Remove from queue"
                      onClick={(e) => { e.stopPropagation(); removeFromQueue(track.id) }}
                    />
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>
    </>
  )
}
